import React from 'react';
import {observer} from 'mobx-react';

import autobind from 'common/decorators/autobind.js';
import CollapsibleSection from 'core/components/CollapsibleSection.js';
import {slugify} from 'core/utils.js';
import {PracticeStoreContext} from 'core/stores/RootStore.js';
import HealthFundPicker from 'core/components/HealthFundPicker.js';
import NoResultsNotice from 'directory/components/NoResultsNotice.js';
import ProfileCard from 'practices/components/ProfileCard.js';
import SearchInput from 'search/components/SearchInput.js';

@observer
class PractitionerList extends React.Component {
    static contextType = PracticeStoreContext;

    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
    }

    renderSearchInput() {
        return (
            <div className="practitioner-search">
                <SearchInput
                    id={'Filter practitioners'}
                    inputLabel="Filter practitioners"
                    inputRef={this.inputRef}
                    onChange={this.context.updateSearchText}
                    placeholder="Name or specialty"
                    value={this.context.searchText || ''}
                    type="text"
                />
            </div>
        );
    }

    renderPracticePractitioners(healthFund) {
        const {
            gapSchemeMembership,
            practice,
            rootStore: {
                paramStore: {isClient, isHfWidget},
            },
            selectHealthFundHandler,
        } = this.context;

        if (healthFund?.id && !isHfWidget) {
            const gapSchemes = gapSchemeMembership
                .filter((pr) => pr.hasGapScheme === true)
                .map((pr) => pr.id);
            const nonGapSchemes = gapSchemeMembership
                .filter((pr) => pr.hasGapScheme === false)
                .map((pr) => pr.id);
            const gapSchemePractitioners = practice.practitioners.filter(
                (pr) => gapSchemes.includes(pr.userprofileId),
            );
            const nonGapSchemePractitioners = practice.practitioners.filter(
                (pr) => nonGapSchemes.includes(pr.userprofileId),
            );
            const participatingStyle = {
                color: `#${healthFund.colorBrand}`,
            };

            return (
                <>
                    {isClient && (
                        <HealthFundPicker
                            handleSelectHealthFund={selectHealthFundHandler}
                            initialOptionText={
                                "I don't have private health insurance"
                            }
                            isHidden={isHfWidget}
                            stopAutoScroll={true}
                        />
                    )}
                    {!isClient && <h2>Practitioners</h2>}
                    {!!gapSchemePractitioners.length && (
                        <>
                            <figure
                                className="card-profile disclaimer"
                                style={participatingStyle}
                            >
                                <img src={healthFund.avatar} />
                                <span style={{color: 'black'}}>
                                    The following practitioners may participate
                                    in a <strong>{healthFund.name}</strong> gap
                                    scheme.
                                </span>
                            </figure>
                            <ul>
                                {gapSchemePractitioners.map((practitioner) => {
                                    return (
                                        <ProfileCard
                                            healthFund={healthFund}
                                            key={practitioner.id}
                                            referralToPractitioner={
                                                practice.referralToPractitioner
                                            }
                                            {...practitioner}
                                        />
                                    );
                                })}
                            </ul>
                        </>
                    )}
                    {!!nonGapSchemePractitioners.length && (
                        <>
                            <div className="disclaimer card-profile non-participating">
                                <span>
                                    The following practitioners may not
                                    participate in a{' '}
                                    <strong>{healthFund.name}</strong> gap
                                    scheme.
                                </span>
                            </div>
                            <ul>
                                {nonGapSchemePractitioners.map(
                                    (practitioner) => {
                                        return (
                                            <ProfileCard
                                                healthFund={healthFund}
                                                key={practitioner.id}
                                                referralToPractitioner={
                                                    practice.referralToPractitioner
                                                }
                                                {...practitioner}
                                                hasFees={false}
                                            />
                                        );
                                    },
                                )}
                            </ul>
                        </>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {!isClient && <h2>Practitioners</h2>}
                    {isClient && (
                        <HealthFundPicker
                            handleSelectHealthFund={selectHealthFundHandler}
                            initialOptionText={
                                "I don't have private health insurance"
                            }
                            isHidden={isHfWidget}
                            stopAutoScroll={true}
                        />
                    )}
                    <ul>
                        {practice.practitioners?.map((practitioner) => {
                            return (
                                <ProfileCard
                                    healthFund={healthFund}
                                    key={practitioner.id}
                                    referralToPractitioner={
                                        practice.referralToPractitioner
                                    }
                                    {...practitioner}
                                />
                            );
                        })}
                    </ul>
                </>
            );
        }
    }

    @autobind
    mainLogoDetector() {
        const {
            rootStore: {
                healthFundStore: {healthFund},
            },
        } = this.context;
        if (healthFund?.id) {
            const nodeList = [
                ...document.querySelectorAll('h3[aria-expanded]'),
            ].find((el) => {
                return (
                    el.getAttribute('aria-expanded') === 'true' &&
                    el.getAttribute('label') === 'true'
                );
            });
            const header = nodeList?.innerText;
            this.context.updateStore({
                mainHeaderSpecialty: header,
            });
        }

        return null;
    }

    renderGapSchemePractitioners() {
        const {
            gapSchemePractitioners,
            searchText,
            practice: {referralToPractitioner},
            rootStore: {
                healthFundStore: {healthFund},
                paramStore: {isHfWidget},
            },
            mainHeaderSpecialty,
        } = this.context;
        if (!gapSchemePractitioners) {
            return null;
        }
        return (
            <>
                {gapSchemePractitioners.map((item) =>
                    Object.keys(item).map((specialty, id) => {
                        const gapSchemeHeaderText = `The following ${specialty} may participate in a <strong>${healthFund?.name}</strong> gap scheme.`;
                        const nonGapSchemeHeaderText = `The following ${specialty} may not participate in a <strong>${healthFund?.name}</strong> gap scheme.`;
                        return (
                            <CollapsibleSection
                                action={this.mainLogoDetector}
                                enabled={!!searchText}
                                key={`collapsible-${id}`}
                                label={item[specialty]?.gapScheme.length > 0}
                                section={specialty}
                            >
                                <ul>
                                    <div>
                                        {item[specialty]?.gapScheme.length >
                                            0 && (
                                            <>
                                                {!isHfWidget && (
                                                    <section
                                                        className="insurance-illustration"
                                                        style={
                                                            healthFund
                                                                ? {
                                                                      color: `#${healthFund?.colorBrand}`,
                                                                  }
                                                                : null
                                                        }
                                                    >
                                                        {mainHeaderSpecialty ===
                                                            specialty && (
                                                            <img
                                                                alt=""
                                                                src={
                                                                    healthFund?.avatar
                                                                }
                                                            />
                                                        )}
                                                        <span
                                                            style={{
                                                                color: 'black',
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: gapSchemeHeaderText,
                                                            }}
                                                        ></span>
                                                    </section>
                                                )}
                                                {item[
                                                    specialty
                                                ]?.gapScheme.map(
                                                    (practitioner) => (
                                                        <ProfileCard
                                                            healthFund={
                                                                healthFund
                                                            }
                                                            key={`${
                                                                practitioner.id
                                                            }-${slugify(
                                                                specialty,
                                                            )}`}
                                                            referralToPractitioner={
                                                                referralToPractitioner
                                                            }
                                                            {...practitioner}
                                                        />
                                                    ),
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div>
                                        {item[specialty]?.nonGapScheme.length >
                                            0 && (
                                            <>
                                                <section className="insurance-illustration non-participating">
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: nonGapSchemeHeaderText,
                                                        }}
                                                    ></span>
                                                </section>
                                                {item[
                                                    specialty
                                                ]?.nonGapScheme.map(
                                                    (practitioner) => {
                                                        return (
                                                            <ProfileCard
                                                                healthFund={
                                                                    healthFund
                                                                }
                                                                key={`${
                                                                    practitioner.id
                                                                }-${slugify(
                                                                    specialty,
                                                                )}`}
                                                                referralToPractitioner={
                                                                    referralToPractitioner
                                                                }
                                                                {...practitioner}
                                                                hasFees={false}
                                                            />
                                                        );
                                                    },
                                                )}
                                            </>
                                        )}
                                    </div>
                                </ul>
                            </CollapsibleSection>
                        );
                    }),
                )}
            </>
        );
    }
    renderNomalPractitioner() {
        const {
            groupedPractitioners,
            healthFund,
            practice: {referralToPractitioner},
            searchText,
        } = this.context;

        return (
            <>
                {groupedPractitioners &&
                    Object.keys(groupedPractitioners)
                        .sort()
                        .map((specialty, ind) => {
                            return (
                                <CollapsibleSection
                                    action={this.mainLogoDetector}
                                    enabled={!!searchText}
                                    key={`collapsible-${ind}`}
                                    section={specialty}
                                >
                                    <ul>
                                        {groupedPractitioners[specialty].map(
                                            (practitioner) => {
                                                return (
                                                    <ProfileCard
                                                        healthFund={healthFund}
                                                        key={`${
                                                            practitioner.id
                                                        }-${slugify(
                                                            specialty,
                                                        )}`}
                                                        referralToPractitioner={
                                                            referralToPractitioner
                                                        }
                                                        {...practitioner}
                                                    />
                                                );
                                            },
                                        )}
                                    </ul>
                                </CollapsibleSection>
                            );
                        })}
            </>
        );
    }

    renderHospitalPractitioners() {
        const {groupedPractitioners} = this.context;
        return (
            <>
                <h2>Practitioners</h2>
                {this.renderSearchInput()}
                {this.context.rootStore.healthFundStore.healthFund?.id
                    ? this.renderGapSchemePractitioners()
                    : this.renderNomalPractitioner()}
                {!groupedPractitioners && (
                    <NoResultsNotice>
                        <p>
                            No results fit your search criteria. Please update
                            your search and try again.
                        </p>
                    </NoResultsNotice>
                )}
            </>
        );
    }

    render() {
        const {healthFund} = this.context.rootStore.healthFundStore;
        return (
            <section className="practitioners">
                {this.context.isHospitalPage
                    ? this.renderHospitalPractitioners()
                    : this.renderPracticePractitioners(healthFund)}
            </section>
        );
    }
}

export default PractitionerList;
